import {
  KgInsightThemeType,
  KNOWLEDGE_GRAPH_FILTERS,
  ThemeSchemaResponse,
} from '@api';
import { AutocompleteInput } from '@design/components/dropdown/autocomplete-input/AutocompleteInput';
import { Dropdown, DropdownProps } from '@design/components/dropdown/Dropdown';
import { Icon } from '@design/components/icon/Icon';
import { themeExposureFilterKey } from '@terminal/config/constants';
import { useSearchThemes } from '@terminal/react-query-hooks/themes/useSearchThemes';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useFilterOptionSearch } from '@terminal/views/screener/use-filter-option-search/useFilterOptionSearch';
import { useScreenerStore } from '@terminal/views/screener/use-screener-store/useScreenerStore';
import clsx from 'clsx';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FollowingSearchEmptyState } from '../../following-search-empty-state/FollowingSearchEmptyState';
import { FollowThemePill } from '../follow-theme-pill/FollowThemePill';
import { FollowThemeRow } from '../follow-theme-row/FollowThemeRow';
import styles from './FollowThemesSearchBar.module.scss';
import {
  createHandlePillClick,
  getSearchResults,
  type SearchResult,
  toSelectedTheme,
} from './utils';

export interface SelectedTheme
  extends Pick<ThemeSchemaResponse, 'id' | 'name'> {
  logo_url?: string;
  type?: KgInsightThemeType;
  isClickable: boolean;
}

export type FollowThemesSearchBarVariant = 'onboarding' | 'drawer';

export interface FollowThemesSearchBarProps {
  selectedThemes: SelectedTheme[];
  onToggle: (theme: SelectedTheme) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: FollowThemesSearchBarVariant;
  onPillClick?: (theme: SelectedTheme) => void;
  className?: string;
}

export const FollowThemesSearchBar = ({
  selectedThemes,
  onToggle,
  isOpen,
  onOpenChange,
  variant = 'onboarding',
  onPillClick,
  className,
}: FollowThemesSearchBarProps) => {
  const { t } = useTranslation('onboarding');
  const isNewThemeSearchEnabled = useFlags(state =>
    state.getFeatureFlagState('KG-786-Macro_Financial_Theme')
  );

  const defaultThemeOptions = useScreenerStore(
    state =>
      state.filterGroups[KNOWLEDGE_GRAPH_FILTERS]?.allFilters.find(
        filter => filter.key === themeExposureFilterKey
      )?.config.options ?? []
  );

  const {
    value: searchValue,
    debouncedSearchTerm: legacyDebouncedSearchTerm,
    data: legacySearchData,
    handleChange,
    onClear,
  } = useFilterOptionSearch({
    filter: themeExposureFilterKey,
    defaultOptions: defaultThemeOptions,
    enabled: !isNewThemeSearchEnabled,
  });
  const { data: newSearchData, debouncedQuery: newDebouncedSearchTerm } =
    useSearchThemes({
      q: searchValue,
      enabled: isNewThemeSearchEnabled,
    });

  const debouncedSearchTerm = isNewThemeSearchEnabled
    ? newDebouncedSearchTerm
    : legacyDebouncedSearchTerm;
  const isSearchActive = !!searchValue.trim() && !!debouncedSearchTerm;

  const searchResults = getSearchResults({
    defaultThemeOptions,
    isNewThemeSearchEnabled,
    isSearchActive,
    legacySearchData,
    newSearchData,
  });

  const isSelected = (themeId: string) =>
    selectedThemes.some(theme => theme.id === themeId);

  const handleOpen = () => onOpenChange(true);

  const handleClose = () => {
    onClear();
    onOpenChange(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onOpenChange(true);
    handleChange(e);
  };

  const handleSelectItem = (item: SearchResult) => {
    onToggle(toSelectedTheme(item, isNewThemeSearchEnabled));
  };

  const handleRemovePill = (theme: SelectedTheme) => () => onToggle(theme);

  const handlePillClick = createHandlePillClick(
    onPillClick,
    isNewThemeSearchEnabled
  );

  const showPills =
    selectedThemes.length > 0 && (variant === 'drawer' || !isOpen);

  const hasContentBelowInput = isOpen || showPills;

  const renderItem: DropdownProps<SearchResult>['renderDropdownItem'] =
    item => (
      <FollowThemeRow
        themeId={item.id}
        themeName={item.name}
        isFollowed={isSelected(item.id)}
        highlighted={isSearchActive ? debouncedSearchTerm : undefined}
        themeType={isNewThemeSearchEnabled ? item.type : undefined}
      />
    );

  const showEmptyState = isSearchActive && searchResults.length === 0;

  const clearButton = searchValue ? (
    <button
      type="button"
      className={styles.clearButton}
      onClick={onClear}
      data-testid="follow-themes-clear"
    >
      {t('onboarding:followThemes.clear')}
    </button>
  ) : null;

  return (
    <div
      className={clsx(
        styles.wrapper,
        isOpen && styles.wrapperActive,
        hasContentBelowInput && styles.wrapperHasContentBelow,
        className
      )}
      data-testid="follow-themes-search-bar"
    >
      <Dropdown<SearchResult>
        className={styles.dropdown}
        dropdownItems={searchResults}
        selectItem={handleSelectItem}
        renderDropdownItem={renderItem}
        shouldCloseOnSelect={false}
        shouldSetActiveOnSelect={false}
        shouldShowItems={isOpen}
        onFocus={variant === 'drawer' ? undefined : handleOpen}
        onClick={handleOpen}
        onOutsideClick={handleClose}
        strategy="fixed"
        offsetValue={{ x: 0, y: 0 }}
        afterInListNode={
          showEmptyState ? <FollowingSearchEmptyState /> : undefined
        }
      >
        <AutocompleteInput
          className={styles.searchInput}
          value={searchValue}
          onChange={handleInputChange}
          leftIcon={<Icon iconName="Search" size={20} />}
          rightIcon={clearButton}
          placeholder={t('onboarding:followThemes.searchPlaceholder')}
          autoFocus={variant === 'drawer'}
        />
      </Dropdown>
      {showPills && (
        <div className={styles.pillsWrapper} data-testid="follow-themes-pills">
          {selectedThemes.map(theme => (
            <FollowThemePill
              key={theme.id}
              themeId={theme.id}
              themeName={theme.name}
              logoUrl={theme.logo_url}
              themeType={isNewThemeSearchEnabled ? theme.type : undefined}
              onRemove={handleRemovePill(theme)}
              onClick={handlePillClick?.(theme)}
              isClickable={isNewThemeSearchEnabled || theme.isClickable}
            />
          ))}
        </div>
      )}
    </div>
  );
};
